import { useState } from "react";
import type { HandoffPriority, HandoffToRole } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { createHandoff } from "@/lib/handoff-api";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-292 [J20] — the "create handoff" form on the clinician patient page. A
// clinician originates a structured handoff/escalation FROM their own role
// (server-derived) TO another role, with a reason, priority, optional context,
// requested decision + deadline. Backed by the patient-scoped POST endpoint
// (CareRelationshipGuard establishes the care relationship).

const PRIORITIES: { value: HandoffPriority; label: string }[] = [
  { value: "low", label: "Låg" },
  { value: "medium", label: "Medel" },
  { value: "high", label: "Hög" },
  { value: "urgent", label: "Akut" },
];

const TO_ROLES: { value: HandoffToRole; label: string }[] = [
  { value: "doctor", label: "Läkare" },
  { value: "coach", label: "Coach" },
];

function PatientHandoffForm({ patientId }: { patientId: string }) {
  const { signOut } = useAuth();
  const [toRole, setToRole] = useState<HandoffToRole>("doctor");
  const [priority, setPriority] = useState<HandoffPriority>("medium");
  const [reason, setReason] = useState("");
  const [context, setContext] = useState("");
  const [requestedDecision, setRequestedDecision] = useState("");
  const [deadline, setDeadline] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(): Promise<void> {
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createHandoff(patientId, {
        toRole,
        priority,
        reason: reason.trim(),
        context: context.trim() || undefined,
        requestedDecision: requestedDecision.trim() || undefined,
        // <input type="date"> gives YYYY-MM-DD; widen to an ISO timestamp.
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
      });
      setReason("");
      setContext("");
      setRequestedDecision("");
      setDeadline("");
      setPriority("medium");
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-md rounded-md border border-surfaceMuted bg-surface p-md">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">Överlämning / eskalering</h2>
        {done ? <span className="text-xs text-brand">Skickad ✓</span> : null}
      </div>

      {error ? (
        <div className="rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-sm">
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
            Till
          </span>
          <select
            value={toRole}
            onChange={(e) => setToRole(e.target.value as HandoffToRole)}
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {TO_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
            Prioritet
          </span>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as HandoffPriority)}
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
            Deadline (valfri)
          </span>
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
        </label>
      </div>

      <label className="flex flex-col gap-xs text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Anledning
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="T.ex. Patienten rapporterar ihållande illamående efter doshöjning"
          className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
      </label>

      <label className="flex flex-col gap-xs text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Kontext (valfri)
        </span>
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          rows={2}
          maxLength={4000}
          className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
      </label>

      <label className="flex flex-col gap-xs text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Efterfrågat beslut (valfritt)
        </span>
        <input
          type="text"
          value={requestedDecision}
          onChange={(e) => setRequestedDecision(e.target.value)}
          maxLength={2000}
          placeholder="T.ex. Hålla eller fortsätta dosen?"
          className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
      </label>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={busy || !reason.trim()}
          className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
        >
          Skicka överlämning
        </button>
      </div>
    </section>
  );
}

// TES-1040 — hidden in the admin's read-only support view: creating a handover is a write.
export const PatientHandoff = hiddenWhenReadOnly(PatientHandoffForm);
